import { updateIntercom } from 'v2/shared/hooks/use-intercom'
import { saveOnboardingAnswers } from 'v2/shared/onboarding/onboarding-answers'
import { getLocalAuth } from 'v2/utils'
import { AnalyticsEvents, setAccountMetadata, track } from 'v2/utils/analytics'
import type { BusinessType, OnboardingFormData } from '../types'
import { buildOnboardingPayload, resolveDestination, type WizardState } from './wizard-data'

interface FinishOnboardingArgs {
  state: WizardState
  businessType: BusinessType | ''
  // The active org, structurally typed so this stays testable without a
  // full RUserProfile fixture.
  org: { onboarding_completed?: boolean; org_auth_id?: string; name?: string } | undefined
  // Whether the org has AI Agents (flow view), which decides where the agent choice lands.
  hasAgents: boolean
  completeOnboarding: (payload: OnboardingFormData, options: { onSuccess: () => void }) => void
  navigate: (to: string, options: { replace: boolean }) => void
  // Both injectable so a test can hold the delayed event and observe it. The repo has no fake
  // timers, and other suites mock.module analytics, so the real track cannot be spied on.
  scheduleEvent?: (fn: () => void, ms: number) => void
  sendEvent?: typeof track
}

// The wizard's "Get started" flow, extracted from the component so the
// rejection path is unit-testable (useProfileQuery is a leaked-mock victim in
// the full suite — CLAUDE.md failure mode 17 — so the component itself is not
// a reliable seam for asserting this).
//
// Navigation happens ONLY on success: on failure the mutation hook leaves
// `onboarding_completed` false and shows the error toast, and navigating
// anyway would let OnboardingGate bounce the user back into a reset
// wizard — they stay put, answers intact, and retry. `replace` keeps
// /onboarding off the history stack; the page has no close control, so the
// browser Back button must not be able to restore it.
export const finishOnboarding = ({
  state,
  businessType,
  org,
  hasAgents,
  completeOnboarding,
  navigate,
  scheduleEvent = setTimeout,
  sendEvent = track,
}: FinishOnboardingArgs): void => {
  const destination = resolveDestination(state, hasAgents)

  // First run for this org: complete the backend onboarding (the flag the
  // post-signup redirect gates on), with the same analytics + account
  // metadata + Intercom side-effects the legacy questionnaire fired.
  //
  // Gated on `!onboarding_completed` rather than `=== false` so an unresolved
  // profile still posts: skipping the call would navigate away and let the
  // post-signup redirect bounce the user straight back into the wizard. The
  // endpoint is idempotent (core short-circuits once the flag is set), and
  // the CTA is disabled while the profile loads, so this is belt-and-braces.
  if (!org?.onboarding_completed && state.build) {
    const payload = buildOnboardingPayload(state, businessType)
    completeOnboarding(payload, {
      // The mutation hook owns the cache flip (onboarding_completed → true
      // on success, per the SER-6080 rule) and the error toast.
      onSuccess: () => {
        // Persist the answers only now that they're final — the banners that
        // personalise from them all live on post-finish pages.
        saveOnboardingAnswers(state)
        // Account-level (group) metadata, keyed on the stable account id so
        // a user's other accounts are not overwritten. product_bucket used to
        // carry business_type; that question was removed from the wizard, so
        // the build choice — the segmentation answer that replaced it — feeds
        // it instead (decided in #3650 review). Repointed rather than dropped
        // because #3505 exists precisely because this property went missing
        // once before.
        setAccountMetadata({
          accountKey: org?.org_auth_id,
          orgName: org?.name,
          productBucket: payload.build,
          cxUseCase: payload.use_case,
          region: getLocalAuth()?.region,
        })
        // One-shot, like setAccountMetadata: this is the only moment the
        // browser sees these answers (the backend logs but never stores
        // them), and Intercom retains them on the contact for targeting.
        //
        // Intercom cannot rename an attribute later, so these key names are
        // fixed. `voice_orchestration_framework` is undefined unless the
        // framework probe actually ran (SIP/audio), dropped rather than blanked
        // so that case stays distinguishable from an explicit answer. The
        // no-code path's task description ships under its own attribute
        // (mirroring core#1367's payload split) so the framework-detail
        // attribute only ever holds what its name says. business_type is
        // dropped (dead key, question removed) — Intercom keeps the attribute
        // on contacts that already have it.
        updateIntercom({
          use_case: payload.use_case,
          voice_orchestration_framework: payload.framework,
          voice_orchestration_framework_detail: payload.framework_detail,
          agent_description: payload.agent_description,
        })
        // The outreach Series reads use_case the moment the event lands, so the attribute
        // write goes first and the event waits 3s. PostHog gets the same delay.
        // business_type is gone from the wizard (the question was removed), so
        // the key is dropped rather than sent as a dead undefined.
        scheduleEvent(
          () =>
            sendEvent(AnalyticsEvents.ACCOUNT_QUESTIONNAIRE_COMPLETED, {
              use_case: payload.use_case,
              build: payload.build,
              framework: payload.framework,
              framework_detail: payload.framework_detail,
              agent_description: payload.agent_description,
            }),
          3_000,
        )
        navigate(destination, { replace: true })
      },
    })
    return
  }

  // Already-onboarded orgs skip the POST but still persist the answers: the
  // product-page banners (and their suppressions, e.g. Voice API hiding the
  // audio banner on /applications) personalise from this key, and re-running
  // the wizard is the user's latest answer either way. Without this, a repeat
  // run navigates with STALE answers and the banners gate on the previous run.
  saveOnboardingAnswers(state)
  navigate(destination, { replace: true })
}
